"use strict";
/**
 * @fileoverview Dual Architecture Transaction Signing
 *
 * This module provides transaction signing capabilities that support both:
 * 1. Legacy architecture: @solana/web3.js Transaction/VersionedTransaction with Keypair/Adapter
 * 2. Kit architecture: @solana/kit TransactionMessage with CryptoKeyPair/Address
 *
 * USAGE EXAMPLES:
 *
 * Legacy Usage (maintains backward compatibility):
 * ```typescript
 * import { Keypair, Transaction } from "@solana/web3.js";
 * import { signTransactionLegacyCompatible } from './transaction';
 *
 * const keypair = Keypair.generate();
 * const transaction = new Transaction();
 * const signed = await signTransactionLegacyCompatible(transaction, keypair);
 * ```
 *
 * Kit Usage (new architecture):
 * ```typescript
 * import { generateKitKeypair, createKitTransaction } from './transaction';
 *
 * const { keypair, address } = await generateKitKeypair();
 * const transaction = await createKitTransaction(connection, address);
 * // Note: Kit signing implementation requires proper transaction message formatting
 * ```
 *
 * Dual Architecture Usage:
 * ```typescript
 * import { signTransaction, DualArchitectureOptions } from './transaction';
 *
 * // Works with both legacy and kit wallets/transactions
 * const signed = await signTransaction(transaction, wallet, {
 *   fallbackToLegacy: true,  // Allow conversion between architectures
 *   preferKitArchitecture: false  // Prefer legacy when possible
 * });
 * ```
 *
 * CURRENT LIMITATIONS:
 * - Kit transaction signing requires proper implementation of transaction message formatting
 * - Conversion between legacy and kit types requires @solana/compat (not yet available)
 * - Kit message signing and sign-in not yet implemented
 *
 * MIGRATION PATH:
 * 1. Use *LegacyCompatible functions for existing code
 * 2. Gradually adopt kit architecture with helper functions
 * 3. Use dual architecture functions when mixing both approaches
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.serializeTransactionForWallet = serializeTransactionForWallet;
exports.isTransactionSigned = isTransactionSigned;
exports.signTransaction = signTransaction;
exports.signAllTransactions = signAllTransactions;
exports.sendTransaction = sendTransaction;
exports.signMessage = signMessage;
exports.signIn = signIn;
exports.createKitTransaction = createKitTransaction;
exports.generateKeyPairSigner = generateKeyPairSigner;
exports.generateKitKeypair = generateKitKeypair;
exports.supportsKitArchitecture = supportsKitArchitecture;
exports.isKitTransaction = isKitTransaction;
exports.createRPCConnection = createRPCConnection;
exports.sendTransactionWithRPC = sendTransactionWithRPC;
exports.signTransactionWithSigner = signTransactionWithSigner;
exports.signAndSendTransaction = signAndSendTransaction;
var web3_js_1 = require("@solana/web3.js");
var index_1 = require("../types/index");
var index_2 = require("../utils/index");
var index_3 = require("../connection/index");
// Import error handling
var errors_1 = require("@hermis/errors");
var bs58_1 = require("bs58");
var tweetnacl_1 = require("tweetnacl");
var offchain_message_1 = require("./offchain-message");
// Import kit functions with correct names based on actual exports
var kit_1 = require("@solana/kit");
var wallet_standard_types_1 = require("../types/wallet-standard-types");
// Note: Compatibility functions would be imported from @solana/compat when available
// For now, we'll implement placeholder conversion functions
/**
 * Helper to safely detect if a transaction is a VersionedTransaction
 * Works even when instanceof checks might not work in test environments
 */
function isVersionedTransaction(transaction) {
    // In test environments, VersionedTransaction might not be properly mocked
    // So we check for the version property which is only present in VersionedTransaction
    return (
    // Check if it has a version property
    'version' in transaction ||
        // Check for an explicit flag that might be set in tests
        '_isVersionedTransaction' in transaction ||
        // Try instanceof check as a backup when available
        (typeof web3_js_1.VersionedTransaction === 'function' && transaction instanceof web3_js_1.VersionedTransaction));
}
/**
 * Helper to detect if a wallet is a legacy wallet (Keypair or Adapter)
 */
function isLegacyWallet(wallet) {
    return (typeof wallet === 'object' &&
        wallet !== null &&
        ('secretKey' in wallet || // Keypair
            ('publicKey' in wallet && 'connected' in wallet)) // Adapter
    );
}
/**
 * Helper to detect if a wallet is a kit wallet (CryptoKeyPair, Address, or MessagePartialSigner)
 * Explicitly checks for the three Kit wallet types instead of using !isLegacyWallet()
 */
function isKitWallet(wallet) {
    return (
    // Check for CryptoKeyPair (has privateKey and publicKey)
    (typeof wallet === 'object' &&
        wallet !== null &&
        'privateKey' in wallet &&
        'publicKey' in wallet) ||
        // Check for Address (string)
        typeof wallet === 'string' ||
        // Check for MessagePartialSigner (has address and signMessages)
        isMessagePartialSigner(wallet));
}
/**
 * Helper to detect if a wallet is a MessagePartialSigner (KeyPairSigner)
 */
function isMessagePartialSigner(wallet) {
    return (wallet &&
        typeof wallet === 'object' &&
        'address' in wallet &&
        'signMessages' in wallet &&
        typeof wallet.signMessages === 'function');
}
/**
 * Helper to detect if a transaction is a TransactionMessage (kit architecture)
 */
function isTransactionMessage(transaction) {
    return (typeof transaction === 'object' &&
        transaction !== null &&
        'instructions' in transaction &&
        'version' in transaction &&
        // Kit transactions have 'version' but it's a string like '0' not a number
        // Legacy VersionedTransaction also has 'version' but also has 'signatures'
        !('signatures' in transaction) &&
        !('recentBlockhash' in transaction));
}
/**
 * Serialize a DualTransaction for Standard Wallet consumption
 * Handles Transaction, VersionedTransaction, and Kit TransactionMessage
 *
 * This helper provides a single point of serialization logic for Standard Wallets,
 * properly handling Kit TransactionMessage by compiling and encoding, while maintaining
 * backward compatibility with legacy Transaction and VersionedTransaction types.
 *
 * @param transaction - Any dual transaction type (Transaction, VersionedTransaction, or TransactionMessage)
 * @returns Serialized transaction bytes ready for Standard Wallet
 */
function serializeTransactionForWallet(transaction) {
    // Kit TransactionMessage: compile then encode
    if (isTransactionMessage(transaction)) {
        var compiled = (0, kit_1.compileTransaction)(transaction);
        var encoder = (0, kit_1.getTransactionEncoder)();
        // Convert ReadonlyUint8Array to Uint8Array for compatibility
        return new Uint8Array(encoder.encode(compiled));
    }
    // Legacy VersionedTransaction
    if (isVersionedTransaction(transaction)) {
        return transaction.serialize();
    }
    // Legacy Transaction
    return transaction.serialize({ verifySignatures: false });
}
/**
 * Helper to detect if a wallet is a CryptoKeyPair
 */
function isCryptoKeyPair(wallet) {
    return (typeof wallet === 'object' &&
        wallet !== null &&
        'privateKey' in wallet &&
        'publicKey' in wallet);
}
/**
 * Helper to detect if a wallet is an Address
 */
function isAddress(wallet) {
    return typeof wallet === 'string';
}
/**
 * Helper to detect the Solana chain from a connection's RPC endpoint
 * Maps known RPC endpoints to their corresponding chain identifiers
 * @param connection The connection to detect the chain from
 * @returns The chain identifier (e.g., 'solana:devnet', 'solana:mainnet')
 */
function getChainFromConnection(connection) {
    // For legacy connections, check the rpcEndpoint property
    if ((0, index_1.isLegacyConnection)(connection)) {
        var endpoint = connection.rpcEndpoint.toLowerCase();
        // Check for devnet
        if (endpoint.includes('devnet')) {
            return 'solana:devnet';
        }
        // Check for testnet
        if (endpoint.includes('testnet')) {
            return 'solana:testnet';
        }
        // Check for mainnet-beta (official endpoint)
        if (endpoint.includes('mainnet-beta') || endpoint.includes('mainnet')) {
            return 'solana:mainnet';
        }
    }
    // Default to mainnet for unknown endpoints or Kit connections
    return 'solana:mainnet';
}
/**
 * Helper to detect if a transaction has valid signatures
 * Returns false for unsigned or partially signed transactions
 * @param transaction The transaction to check
 * @returns true if the transaction has at least one valid signature
 */
function isTransactionSigned(transaction) {
    // Safety check: only works with objects that have a signatures property
    if (!transaction || typeof transaction !== 'object' || !('signatures' in transaction)) {
        return false;
    }
    var signatures = transaction.signatures;
    // No signatures array means unsigned
    if (!signatures || signatures.length === 0) {
        return false;
    }
    // Check if at least one signature is present and non-zero
    // A zero signature (all bytes are 0) indicates a placeholder, not a real signature
    var hasValidSig = signatures.some(function (sig) {
        // Handle both signature formats (Uint8Array or {signature: Uint8Array})
        var sigBytes = sig.signature !== undefined ? sig.signature : sig;
        if (!sigBytes || sigBytes.length === 0) {
            return false;
        }
        // Check if all bytes are zero (placeholder signature)
        for (var i = 0; i < sigBytes.length; i++) {
            if (sigBytes[i] !== 0) {
                return true; // Found a non-zero byte, this is a real signature
            }
        }
        return false; // All bytes are zero, this is a placeholder
    });
    return hasValidSig;
}
/**
 * Signs a transaction with improved signer compatibility
 * Supports both legacy and kit architectures
 */
function signTransaction(transaction_1, walletOrSigner_1) {
    return __awaiter(this, arguments, Promise, function (transaction, walletOrSigner, options) {
        var legacyKeypair, legacyTransaction;
        if (options === void 0) { options = {}; }
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!isMessagePartialSigner(walletOrSigner)) return [3 /*break*/, 5];
                    if (!isTransactionMessage(transaction)) return [3 /*break*/, 2];
                    return [4 /*yield*/, signTransactionWithKitSigner(transaction, walletOrSigner)];
                case 1: 
                // Kit signer with kit transaction
                return [2 /*return*/, _a.sent()];
                case 2:
                    if (!(options.fallbackToLegacy !== false)) return [3 /*break*/, 4];
                    return [4 /*yield*/, signTransactionLegacyWithKitSigner(transaction, walletOrSigner)];
                case 3: return [2 /*return*/, _a.sent()];
                case 4: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__KIT__LEGACY_MODE_INCOMPATIBLE, {
                    feature: 'Kit signer signing legacy transaction',
                    suggestion: 'Enable fallbackToLegacy option'
                });
                case 5:
                    if (!isKitWallet(walletOrSigner)) return [3 /*break*/, 15];
                    if (!isTransactionMessage(transaction)) return [3 /*break*/, 9];
                    if (!isCryptoKeyPair(walletOrSigner)) return [3 /*break*/, 7];
                    return [4 /*yield*/, signTransactionKit(transaction, walletOrSigner)];
                case 6: 
                // Both wallet and transaction are kit architecture - use kit signing
                return [2 /*return*/, _a.sent()];
                case 7: 
                // Address without private key - cannot sign
                throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__PRIVATE_KEY_UNAVAILABLE, {});
                case 8: return [3 /*break*/, 15];
                case 9:
                    if (!(options.fallbackToLegacy !== false)) return [3 /*break*/, 14];
                    if (!isCryptoKeyPair(walletOrSigner)) return [3 /*break*/, 12];
                    return [4 /*yield*/, convertCryptoKeyPairToKeypair(walletOrSigner)];
                case 10:
                    legacyKeypair = _a.sent();
                    return [4 /*yield*/, signTransactionLegacy(transaction, legacyKeypair)];
                case 11: return [2 /*return*/, _a.sent()];
                case 12: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__KIT__LEGACY_CONVERSION_FAILED, {
                    sourceType: 'Address',
                    targetType: 'LegacyWallet',
                    reason: 'Address type cannot be converted to legacy wallet for signing'
                });
                case 13: return [3 /*break*/, 15];
                case 14: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__KIT__LEGACY_MODE_INCOMPATIBLE, {
                    feature: 'Kit wallet signing legacy transaction',
                    suggestion: 'Enable fallbackToLegacy option'
                });
                case 15:
                    if (!isTransactionMessage(transaction)) return [3 /*break*/, 20];
                    if (!(options.preferKitArchitecture === false || options.fallbackToLegacy !== false)) return [3 /*break*/, 18];
                    return [4 /*yield*/, convertTransactionMessageToLegacy(transaction)];
                case 16:
                    legacyTransaction = _a.sent();
                    return [4 /*yield*/, signTransactionLegacy(legacyTransaction, walletOrSigner)];
                case 17: return [2 /*return*/, _a.sent()];
                case 18: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__KIT__LEGACY_MODE_INCOMPATIBLE, {
                    feature: 'Legacy wallet signing Kit transaction',
                    suggestion: 'Enable conversion or use a Kit-compatible wallet'
                });
                case 19: return [3 /*break*/, 22];
                case 20: return [4 /*yield*/, signTransactionLegacy(transaction, walletOrSigner)];
                case 21: 
                // Both wallet and transaction are legacy architecture
                return [2 /*return*/, _a.sent()];
                case 22: return [2 /*return*/];
            }
        });
    });
}
/**
 * Legacy transaction signing implementation
 */
function signTransactionLegacy(transaction, wallet) {
    return __awaiter(this, void 0, Promise, function () {
        var feature, account, accountPublicKey, transactionBytes, result, error_1, signedTransactionBytes;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!('secretKey' in wallet)) return [3 /*break*/, 4];
                    if (!!isVersionedTransaction(transaction)) return [3 /*break*/, 1];
                    // For legacy Transaction, we can sign directly
                    transaction.sign(wallet);
                    return [2 /*return*/, transaction];
                case 1: return [4 /*yield*/, signVersionedTransactionWithKeypair(transaction, wallet)];
                case 2: 
                // For VersionedTransaction, we need manual signing logic
                return [2 /*return*/, _a.sent()];
                case 3: return [3 /*break*/, 12];
                case 4:
                    if (!("features" in wallet)) return [3 /*break*/, 9];
                    feature = wallet.features[wallet_standard_types_1.SolanaSignTransactionMethod];
                    if (!feature || typeof feature.signTransaction !== 'function') {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__STANDARD_WALLET__FEATURE_NOT_FOUND, { featureName: 'signTransaction', walletName: wallet.name || 'Unknown wallet' });
                    }
                    account = wallet.accounts[0];
                    if (!account) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__STANDARD_WALLET__ACCOUNT_NOT_FOUND, { walletName: wallet.name || 'Unknown wallet' });
                    }
                    accountPublicKey = new web3_js_1.PublicKey(account.publicKey);
                    // Ensure transaction has feePayer set (only for legacy Transaction, not Kit TransactionMessage)
                    if (!isVersionedTransaction(transaction) && !isTransactionMessage(transaction) && !transaction.feePayer) {
                        transaction.feePayer = accountPublicKey;
                    }
                    transactionBytes = void 0;
                    try {
                        transactionBytes = serializeTransactionForWallet(transaction);
                    }
                    catch (error) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__TRANSACTION__SERIALIZATION_FAILED, {
                            transactionType: isTransactionMessage(transaction) ? 'Kit TransactionMessage' : isVersionedTransaction(transaction) ? 'VersionedTransaction' : 'Transaction',
                            reason: 'Serialization failed for Standard Wallet',
                            originalError: error instanceof Error ? error.message : String(error)
                        }, error instanceof Error ? error : undefined);
                    }
                    result = void 0;
                    _a.label = 5;
                case 5:
                    _a.trys.push([5, 7, , 8]);
                    return [4 /*yield*/, feature.signTransaction({
                            transaction: transactionBytes,
                            account: account
                        })];
                case 6:
                    result = _a.sent();
                    return [3 /*break*/, 8];
                case 7:
                    error_1 = _a.sent();
                    // Check if user rejected the transaction
                    if ((0, errors_1.isUserRejection)(error_1)) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__WALLET_INTERACTION__USER_REJECTED_TRANSACTION, { walletName: wallet.name || 'Unknown wallet' });
                    }
                    // For other errors, wrap with generic signing error
                    throw (0, errors_1.wrapError)(error_1, errors_1.HERMIS_ERROR__SIGNING__TRANSACTION_FAILED, {
                        reason: 'Failed to sign transaction with Standard Wallet'
                    });
                case 8:
                    if (!result || !result[0] || !result[0].signedTransaction) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__TRANSACTION_FAILED, {
                            reason: 'No signed transaction returned from wallet'
                        });
                    }
                    // Deserialize the signed transaction
                    try {
                        signedTransactionBytes = result[0].signedTransaction;
                        if (isVersionedTransaction(transaction)) {
                            return [2 /*return*/, web3_js_1.VersionedTransaction.deserialize(signedTransactionBytes)];
                        }
                        else {
                            return [2 /*return*/, web3_js_1.Transaction.from(signedTransactionBytes)];
                        }
                    }
                    catch (error) {
                        throw (0, errors_1.wrapError)(error, errors_1.HERMIS_ERROR__TRANSACTION__DESERIALIZATION_FAILED, {
                            reason: 'Failed to deserialize signed transaction'
                        });
                    }
                    return [3 /*break*/, 12];
                case 9:
                    // It's an Adapter
                    if (!wallet.publicKey) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__WALLET_CONNECTION__NOT_CONNECTED, { walletName: wallet.name || 'Unknown wallet' });
                    }
                    // For versioned transactions, we need to check if the adapter supports it
                    if (isVersionedTransaction(transaction) &&
                        wallet.supportedTransactionVersions &&
                        !wallet.supportedTransactionVersions.has(transaction.version)) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__TRANSACTION__VERSION_NOT_SUPPORTED, {
                            walletName: wallet.name || 'Unknown wallet',
                            version: String(transaction.version)
                        });
                    }
                    if (!('signTransaction' in wallet && typeof wallet.signTransaction === 'function')) return [3 /*break*/, 11];
                    return [4 /*yield*/, wallet.signTransaction(transaction)];
                case 10: return [2 /*return*/, _a.sent()];
                case 11: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__TRANSACTION_FAILED, {
                    reason: 'Wallet adapter does not support direct transaction signing'
                });
                case 12: return [2 /*return*/];
            }
        });
    });
}
/**
 * Convert CryptoKeyPair to legacy Keypair
 * This extracts the private key bytes and creates a @solana/web3.js Keypair
 */
function convertCryptoKeyPairToKeypair(cryptoKeyPair) {
    return __awaiter(this, void 0, Promise, function () {
        var privateKeyBytes, publicKeyBytes, _a, secretKeyBytes, error_2;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 3, , 4]);
                    return [4 /*yield*/, attemptPrivateKeyExtraction(cryptoKeyPair)];
                case 1:
                    privateKeyBytes = _b.sent();
                    if (!privateKeyBytes) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__KEYPAIR_CONVERSION_FAILED, {
                            reason: 'Cannot extract private key from CryptoKeyPair - key is not extractable'
                        });
                    }
                    _a = Uint8Array.bind;
                    return [4 /*yield*/, crypto.subtle.exportKey("raw", cryptoKeyPair.publicKey)];
                case 2:
                    publicKeyBytes = new (_a.apply(Uint8Array, [void 0, _b.sent()]))();
                    secretKeyBytes = new Uint8Array(64);
                    secretKeyBytes.set(privateKeyBytes, 0);
                    secretKeyBytes.set(publicKeyBytes, 32);
                    // Create and return the Keypair
                    return [2 /*return*/, web3_js_1.Keypair.fromSecretKey(secretKeyBytes)];
                case 3:
                    error_2 = _b.sent();
                    throw (0, errors_1.wrapError)(error_2, errors_1.HERMIS_ERROR__SIGNING__KEYPAIR_CONVERSION_FAILED, {
                        reason: 'Failed to convert CryptoKeyPair to Keypair'
                    });
                case 4: return [2 /*return*/];
            }
        });
    });
}
/**
 * Convert CryptoKeyPair to MessagePartialSigner for kit message signing
 * This is the complete implementation that properly converts CryptoKeyPair to a working MessagePartialSigner
 *
 * IMPLEMENTATION STRATEGY:
 * 1. Try to extract private key via multiple methods (raw, pkcs8, jwk)
 * 2. If extraction fails, create a bridge signer that uses the CryptoKeyPair directly
 * 3. Provide a working MessagePartialSigner interface regardless of extraction success
 */
function convertCryptoKeyPairToMessageSigner(cryptoKeyPair) {
    return __awaiter(this, void 0, Promise, function () {
        var privateKeyBytes, error_3;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 5, , 6]);
                    // Validate input CryptoKeyPair
                    if (!cryptoKeyPair || !cryptoKeyPair.privateKey || !cryptoKeyPair.publicKey) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__KEYPAIR_CONVERSION_FAILED, {
                            reason: 'Invalid CryptoKeyPair: missing private or public key'
                        });
                    }
                    // Check key algorithm
                    if (cryptoKeyPair.privateKey.algorithm.name !== "Ed25519") {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__KEYPAIR_CONVERSION_FAILED, {
                            reason: "Unsupported key algorithm: ".concat(cryptoKeyPair.privateKey.algorithm.name, ". Only Ed25519 is supported.")
                        });
                    }
                    // Check key usage
                    if (!cryptoKeyPair.privateKey.usages.includes("sign")) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__KEYPAIR_CONVERSION_FAILED, {
                            reason: 'Private key does not support signing'
                        });
                    }
                    return [4 /*yield*/, attemptPrivateKeyExtraction(cryptoKeyPair)];
                case 1:
                    privateKeyBytes = _a.sent();
                    if (!privateKeyBytes) return [3 /*break*/, 3];
                    return [4 /*yield*/, (0, kit_1.createKeyPairSignerFromPrivateKeyBytes)(privateKeyBytes)];
                case 2: 
                // Success! Create a proper kit signer from the extracted bytes
                return [2 /*return*/, _a.sent()];
                case 3: return [4 /*yield*/, createBridgeMessageSigner(cryptoKeyPair)];
                case 4: 
                // Strategy 2: Create a bridge signer that uses the CryptoKeyPair directly
                return [2 /*return*/, _a.sent()];
                case 5:
                    error_3 = _a.sent();
                    throw (0, errors_1.wrapError)(error_3, errors_1.HERMIS_ERROR__SIGNING__KEYPAIR_CONVERSION_FAILED, {
                        reason: 'Failed to convert CryptoKeyPair to MessageSigner'
                    });
                case 6: return [2 /*return*/];
            }
        });
    });
}
/**
 * Attempt to extract private key bytes from CryptoKeyPair using multiple strategies
 * Returns null if extraction fails, which is expected for non-extractable keys
 */
function attemptPrivateKeyExtraction(cryptoKeyPair) {
    return __awaiter(this, void 0, Promise, function () {
        var strategies, _i, strategies_1, strategy, result, error_4;
        var _this = this;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    strategies = [
                        // Strategy 1: Try raw export (works if key is extractable)
                        function () { return __awaiter(_this, void 0, void 0, function () {
                            var rawKey;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0: return [4 /*yield*/, crypto.subtle.exportKey("raw", cryptoKeyPair.privateKey)];
                                    case 1:
                                        rawKey = _a.sent();
                                        return [2 /*return*/, new Uint8Array(rawKey)];
                                }
                            });
                        }); },
                        // Strategy 2: Try PKCS#8 export and extract the Ed25519 key
                        function () { return __awaiter(_this, void 0, void 0, function () {
                            var pkcs8Key, pkcs8Bytes, privateKeyBytes;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0: return [4 /*yield*/, crypto.subtle.exportKey("pkcs8", cryptoKeyPair.privateKey)];
                                    case 1:
                                        pkcs8Key = _a.sent();
                                        pkcs8Bytes = new Uint8Array(pkcs8Key);
                                        // Ed25519 private keys in PKCS#8 format: extract last 32 bytes
                                        if (pkcs8Bytes.length >= 32) {
                                            privateKeyBytes = pkcs8Bytes.slice(-32);
                                            if (privateKeyBytes.length === 32) {
                                                return [2 /*return*/, privateKeyBytes];
                                            }
                                        }
                                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__KEYPAIR_CONVERSION_FAILED, {
                                            reason: 'Invalid PKCS#8 format for Ed25519 key'
                                        });
                                }
                            });
                        }); },
                        // Strategy 3: Try JWK export and decode
                        function () { return __awaiter(_this, void 0, void 0, function () {
                            var jwk, base64url, base64, padding, privateKeyBytes;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0: return [4 /*yield*/, crypto.subtle.exportKey("jwk", cryptoKeyPair.privateKey)];
                                    case 1:
                                        jwk = _a.sent();
                                        if (!jwk.d) {
                                            throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__KEYPAIR_CONVERSION_FAILED, {
                                                reason: "JWK does not contain private key component 'd'"
                                            });
                                        }
                                        base64url = jwk.d;
                                        base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
                                        padding = '='.repeat((4 - base64.length % 4) % 4);
                                        privateKeyBytes = Uint8Array.from(atob(base64 + padding), function (c) { return c.charCodeAt(0); });
                                        if (privateKeyBytes.length !== 32) {
                                            throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__KEYPAIR_CONVERSION_FAILED, {
                                                reason: "Invalid Ed25519 private key length: ".concat(privateKeyBytes.length)
                                            });
                                        }
                                        return [2 /*return*/, privateKeyBytes];
                                }
                            });
                        }); }
                    ];
                    _i = 0, strategies_1 = strategies;
                    _a.label = 1;
                case 1:
                    if (!(_i < strategies_1.length)) return [3 /*break*/, 6];
                    strategy = strategies_1[_i];
                    _a.label = 2;
                case 2:
                    _a.trys.push([2, 4, , 5]);
                    return [4 /*yield*/, strategy()];
                case 3:
                    result = _a.sent();
                    return [2 /*return*/, result];
                case 4:
                    error_4 = _a.sent();
                    return [3 /*break*/, 5];
                case 5:
                    _i++;
                    return [3 /*break*/, 1];
                case 6: return [2 /*return*/, null];
            }
        });
    });
}
/**
 * Create a bridge MessagePartialSigner that uses the CryptoKeyPair directly
 * This works even when the private key is not extractable
 */
function createBridgeMessageSigner(cryptoKeyPair) {
    return __awaiter(this, void 0, Promise, function () {
        var publicKeyBytes, _a, addressString, bridgeSigner;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _a = Uint8Array.bind;
                    return [4 /*yield*/, crypto.subtle.exportKey("raw", cryptoKeyPair.publicKey)];
                case 1:
                    publicKeyBytes = new (_a.apply(Uint8Array, [void 0, _b.sent()]))();
                    addressString = bs58_1.default.encode(publicKeyBytes);
                    bridgeSigner = {
                        address: addressString, // Cast to Address type
                        signMessages: function (messages) {
                            return __awaiter(this, void 0, Promise, function () {
                                var signatureDictionaries, _i, messages_1, message, messageBytes, signature, signatureBytes, signatureDictionary, error_5;
                                var _a;
                                return __generator(this, function (_b) {
                                    switch (_b.label) {
                                        case 0:
                                            _b.trys.push([0, 5, , 6]);
                                            signatureDictionaries = [];
                                            _i = 0, messages_1 = messages;
                                            _b.label = 1;
                                        case 1:
                                            if (!(_i < messages_1.length)) return [3 /*break*/, 4];
                                            message = messages_1[_i];
                                            messageBytes = void 0;
                                            if (message && typeof message === 'object' && 'content' in message) {
                                                // It's a SignableMessage with content property
                                                messageBytes = message.content;
                                            }
                                            else if (message instanceof Uint8Array) {
                                                // It's raw message bytes
                                                messageBytes = message;
                                            }
                                            else {
                                                throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__MESSAGE_FAILED, {
                                                    reason: 'Unsupported message format for bridge signer'
                                                });
                                            }
                                            return [4 /*yield*/, crypto.subtle.sign("Ed25519", cryptoKeyPair.privateKey, messageBytes)];
                                        case 2:
                                            signature = _b.sent();
                                            signatureBytes = new Uint8Array(signature);
                                            signatureDictionary = (_a = {},
                                                _a[addressString] = signatureBytes,
                                                _a);
                                            signatureDictionaries.push(signatureDictionary);
                                            _b.label = 3;
                                        case 3:
                                            _i++;
                                            return [3 /*break*/, 1];
                                        case 4: return [2 /*return*/, signatureDictionaries];
                                        case 5:
                                            error_5 = _b.sent();
                                            throw (0, errors_1.wrapError)(error_5, errors_1.HERMIS_ERROR__SIGNING__MESSAGE_FAILED, {
                                                reason: 'Bridge signer failed to sign messages'
                                            });
                                        case 6: return [2 /*return*/];
                                    }
                                });
                            });
                        }
                    };
                    return [2 /*return*/, bridgeSigner];
            }
        });
    });
}
/**
 * Alternative method for creating MessagePartialSigner from private key bytes
 * This is useful when you need to extract private key from CryptoKeyPair first
 */
function createMessageSignerFromPrivateKeyBytes(privateKeyBytes) {
    return __awaiter(this, void 0, Promise, function () {
        var fullKeypairBytes, keypairSigner, error_6;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    if (privateKeyBytes.length !== 32) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__KEYPAIR_CONVERSION_FAILED, {
                            reason: "Invalid private key length: expected 32 bytes, got ".concat(privateKeyBytes.length)
                        });
                    }
                    fullKeypairBytes = new Uint8Array(64);
                    fullKeypairBytes.set(privateKeyBytes, 0);
                    return [4 /*yield*/, (0, kit_1.createKeyPairSignerFromPrivateKeyBytes)(privateKeyBytes)];
                case 1:
                    keypairSigner = _a.sent();
                    return [2 /*return*/, keypairSigner];
                case 2:
                    error_6 = _a.sent();
                    throw (0, errors_1.wrapError)(error_6, errors_1.HERMIS_ERROR__SIGNING__KEYPAIR_CONVERSION_FAILED, {
                        reason: 'Failed to create MessageSigner from private key bytes'
                    });
                case 3: return [2 /*return*/];
            }
        });
    });
}
/**
 * Utility function to extract private key bytes from CryptoKeyPair
 * This is useful for debugging or when you need the raw private key
 * Uses the same multi-strategy approach as the main conversion function
 */
function extractPrivateKeyBytes(cryptoKeyPair) {
    return __awaiter(this, void 0, Promise, function () {
        var privateKeyBytes;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, attemptPrivateKeyExtraction(cryptoKeyPair)];
                case 1:
                    privateKeyBytes = _a.sent();
                    if (!privateKeyBytes) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__KEYPAIR_CONVERSION_FAILED, {
                            reason: 'Failed to extract private key bytes: key is not extractable'
                        });
                    }
                    return [2 /*return*/, privateKeyBytes];
            }
        });
    });
}
/**
 * Convert TransactionMessage to legacy Transaction
 * This converts a kit TransactionMessage to a @solana/web3.js Transaction
 */
function convertTransactionMessageToLegacy(transactionMessage) {
    return __awaiter(this, void 0, Promise, function () {
        var transaction, _i, _a, instruction, legacyInstruction, feePayerValue, feePayerAddress, lifetimeConstraint;
        var _b;
        return __generator(this, function (_c) {
            try {
                transaction = new web3_js_1.Transaction();
                // Extract transaction properties - skip compilation for legacy conversion
                // Convert kit instructions to legacy format
                if ('instructions' in transactionMessage && Array.isArray(transactionMessage.instructions)) {
                    for (_i = 0, _a = transactionMessage.instructions; _i < _a.length; _i++) {
                        instruction = _a[_i];
                        legacyInstruction = {
                            programId: new web3_js_1.PublicKey(instruction.programAddress),
                            keys: ((_b = instruction.accounts) === null || _b === void 0 ? void 0 : _b.map(function (account) {
                                var _a, _b, _c, _d;
                                // Handle both numeric roles (Kit standard: 0=readonly, 1=writable, 2=signer, 3=both)
                                // and string/array roles (legacy format)
                                var isSigner = false;
                                var isWritable = false;
                                if (typeof account.role === 'number') {
                                    // Numeric role (Kit format): bitflags
                                    // bit 0 (value 1): writable
                                    // bit 1 (value 2): signer
                                    // value 3 (1|2): both writable and signer
                                    isWritable = (account.role & 1) !== 0;
                                    isSigner = (account.role & 2) !== 0;
                                }
                                else if (account.role) {
                                    // String/array role (legacy format)
                                    isSigner = ((_b = (_a = account.role) === null || _a === void 0 ? void 0 : _a.includes) === null || _b === void 0 ? void 0 : _b.call(_a, 'signer')) || false;
                                    isWritable = ((_d = (_c = account.role) === null || _c === void 0 ? void 0 : _c.includes) === null || _d === void 0 ? void 0 : _d.call(_c, 'writable')) || false;
                                }
                                return {
                                    pubkey: new web3_js_1.PublicKey(account.address),
                                    isSigner: isSigner,
                                    isWritable: isWritable,
                                };
                            })) || [],
                            data: instruction.data || Buffer.alloc(0),
                        };
                        transaction.add(legacyInstruction);
                    }
                }
                // Set transaction properties if available
                if ('feePayer' in transactionMessage && transactionMessage.feePayer) {
                    feePayerValue = transactionMessage.feePayer;
                    feePayerAddress = typeof feePayerValue === 'string'
                        ? feePayerValue
                        : feePayerValue.address;
                    transaction.feePayer = new web3_js_1.PublicKey(feePayerAddress);
                }
                lifetimeConstraint = transactionMessage.lifetimeConstraint || transactionMessage.lifetimeConstraints;
                if (lifetimeConstraint) {
                    if (lifetimeConstraint.blockhash) {
                        transaction.recentBlockhash = lifetimeConstraint.blockhash;
                    }
                }
                return [2 /*return*/, transaction];
            }
            catch (error) {
                throw (0, errors_1.wrapError)(error, errors_1.HERMIS_ERROR__KIT__LEGACY_CONVERSION_FAILED, {
                    sourceType: 'TransactionMessage',
                    targetType: 'Transaction',
                    reason: 'Failed to convert TransactionMessage to Transaction'
                });
            }
            return [2 /*return*/];
        });
    });
}
/**
 * Signs multiple transactions using the specified wallet (supports both legacy and kit architectures)
 * @param transactions The array of transactions to sign
 * @param wallet The wallet to sign with (can be Keypair, Adapter, CryptoKeyPair, or Address)
 * @param options Optional configuration for dual architecture behavior
 * @returns A promise that resolves to an array of signed transactions
 */
function signAllTransactions(transactions_1, wallet_1) {
    return __awaiter(this, arguments, Promise, function (transactions, wallet, options) {
        var legacyKeypair, signedTransactions, _i, transactions_2, transaction, legacyTransaction, signed, signed, signedTransactions, _a, transactions_3, transaction, legacyTransaction, signed, signed;
        if (options === void 0) { options = {}; }
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!isKitWallet(wallet)) return [3 /*break*/, 13];
                    if (!transactions.every(isTransactionMessage)) return [3 /*break*/, 1];
                    // Both wallet and all transactions are kit architecture
                    if (isCryptoKeyPair(wallet)) {
                        // Kit batch signing requires proper transaction message formatting
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__TRANSACTION__BATCH_SIGNING_NOT_IMPLEMENTED, {
                            walletType: 'Kit'
                        });
                    }
                    else {
                        // Address without private key - cannot sign
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__PRIVATE_KEY_UNAVAILABLE, {});
                    }
                    return [3 /*break*/, 13];
                case 1:
                    if (!(options.fallbackToLegacy !== false)) return [3 /*break*/, 12];
                    if (!isCryptoKeyPair(wallet)) return [3 /*break*/, 10];
                    return [4 /*yield*/, convertCryptoKeyPairToKeypair(wallet)];
                case 2:
                    legacyKeypair = _b.sent();
                    signedTransactions = [];
                    _i = 0, transactions_2 = transactions;
                    _b.label = 3;
                case 3:
                    if (!(_i < transactions_2.length)) return [3 /*break*/, 9];
                    transaction = transactions_2[_i];
                    if (!isTransactionMessage(transaction)) return [3 /*break*/, 6];
                    return [4 /*yield*/, convertTransactionMessageToLegacy(transaction)];
                case 4:
                    legacyTransaction = _b.sent();
                    return [4 /*yield*/, signTransactionLegacy(legacyTransaction, legacyKeypair)];
                case 5:
                    signed = _b.sent();
                    // Convert back to TransactionMessage if needed
                    throw new errors_1.HermisError(errors_1.HERMIS_ERROR__KIT__LEGACY_MODE_INCOMPATIBLE, {
                        feature: 'Converting signed legacy transaction back to Kit TransactionMessage',
                        suggestion: 'This conversion is not yet implemented'
                    });
                case 6: return [4 /*yield*/, signTransactionLegacy(transaction, legacyKeypair)];
                case 7:
                    signed = _b.sent();
                    signedTransactions.push(signed);
                    _b.label = 8;
                case 8:
                    _i++;
                    return [3 /*break*/, 3];
                case 9: return [2 /*return*/, signedTransactions];
                case 10: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__KIT__LEGACY_CONVERSION_FAILED, {
                    sourceType: 'Address',
                    targetType: 'LegacyWallet',
                    reason: 'Address type cannot be converted to legacy wallet for signing'
                });
                case 11: return [3 /*break*/, 13];
                case 12: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__KIT__LEGACY_MODE_INCOMPATIBLE, {
                    feature: 'Kit wallet signing legacy transactions',
                    suggestion: 'Enable fallbackToLegacy option'
                });
                case 13:
                    if (!transactions.every(function (tx) { return !isTransactionMessage(tx); })) return [3 /*break*/, 15];
                    return [4 /*yield*/, signAllTransactionsLegacy(transactions, wallet)];
                case 14: 
                // Both wallet and all transactions are legacy architecture
                return [2 /*return*/, _b.sent()];
                case 15:
                    if (!(options.preferKitArchitecture === false || options.fallbackToLegacy !== false)) return [3 /*break*/, 23];
                    signedTransactions = [];
                    _a = 0, transactions_3 = transactions;
                    _b.label = 16;
                case 16:
                    if (!(_a < transactions_3.length)) return [3 /*break*/, 22];
                    transaction = transactions_3[_a];
                    if (!isTransactionMessage(transaction)) return [3 /*break*/, 19];
                    return [4 /*yield*/, convertTransactionMessageToLegacy(transaction)];
                case 17:
                    legacyTransaction = _b.sent();
                    return [4 /*yield*/, signTransactionLegacy(legacyTransaction, wallet)];
                case 18:
                    signed = _b.sent();
                    // Convert back to TransactionMessage if needed
                    // throw new Error('Legacy to kit transaction conversion not yet implemented');
                    signedTransactions.push(signed);
                    return [3 /*break*/, 21];
                case 19: return [4 /*yield*/, signTransactionLegacy(transaction, wallet)];
                case 20:
                    signed = _b.sent();
                    signedTransactions.push(signed);
                    _b.label = 21;
                case 21:
                    _a++;
                    return [3 /*break*/, 16];
                case 22: return [2 /*return*/, signedTransactions];
                case 23: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__KIT__LEGACY_MODE_INCOMPATIBLE, {
                    feature: 'Legacy wallet signing Kit transactions',
                    suggestion: 'Enable conversion or use a Kit-compatible wallet'
                });
            }
        });
    });
}
/**
 * Legacy implementation for signing multiple transactions
 */
function signAllTransactionsLegacy(transactions, wallet) {
    return __awaiter(this, void 0, Promise, function () {
        var signedTransactions, feature, signedTransactions, _i, transactions_1, transaction, account, accountPublicKey, transactionBytes, result, error_7, signedTransactionBytes, _a, transactions_4, transaction, signedTransactions, _b, transactions_5, transaction, signed;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    if (!('secretKey' in wallet)) return [3 /*break*/, 1];
                    signedTransactions = transactions.map(function (transaction) {
                        // Check if transaction is a Transaction or VersionedTransaction
                        if (isVersionedTransaction(transaction)) {
                            // For VersionedTransaction, use our custom signing logic
                            return signVersionedTransactionWithKeypair(transaction, wallet);
                        }
                        // Create a copy to avoid modifying the original
                        var txCopy = web3_js_1.Transaction.from(transaction.serialize());
                        // Sign the transaction
                        txCopy.sign(wallet);
                        return txCopy;
                    });
                    return [2 /*return*/, signedTransactions];
                case 1:
                    if (!("features" in wallet)) return [3 /*break*/, 9];
                    feature = wallet.features[wallet_standard_types_1.SolanaSignTransactionMethod];
                    if (!feature || typeof feature.signTransaction !== 'function') {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__STANDARD_WALLET__FEATURE_NOT_FOUND, {
                            featureName: 'signTransaction',
                            walletName: 'Standard Wallet'
                        });
                    }
                    signedTransactions = [];
                    _i = 0, transactions_1 = transactions;
                    _c.label = 2;
                case 2:
                    if (!(_i < transactions_1.length)) return [3 /*break*/, 8];
                    transaction = transactions_1[_i];
                    account = wallet.accounts[0];
                    if (!account) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__STANDARD_WALLET__ACCOUNT_NOT_FOUND, {
                            walletName: 'Standard Wallet'
                        });
                    }
                    accountPublicKey = new web3_js_1.PublicKey(account.publicKey);
                    // Ensure transaction has feePayer set (only for legacy Transaction, not Kit TransactionMessage)
                    if (!isVersionedTransaction(transaction) && !isTransactionMessage(transaction) && !transaction.feePayer) {
                        transaction.feePayer = accountPublicKey;
                    }
                    transactionBytes = void 0;
                    try {
                        transactionBytes = serializeTransactionForWallet(transaction);
                    }
                    catch (error) {
                        throw (0, errors_1.wrapError)(error, errors_1.HERMIS_ERROR__TRANSACTION__SERIALIZATION_FAILED, {
                            transactionType: isVersionedTransaction(transaction) ? 'VersionedTransaction' : 'Transaction',
                            reason: 'Failed to serialize transaction for signing'
                        });
                    }
                    result = void 0;
                    _c.label = 3;
                case 3:
                    _c.trys.push([3, 5, , 6]);
                    return [4 /*yield*/, feature.signTransaction({
                            transaction: transactionBytes,
                            account: account
                        })];
                case 4:
                    result = _c.sent();
                    return [3 /*break*/, 6];
                case 5:
                    error_7 = _c.sent();
                    // Check if user rejected the transaction
                    if ((0, errors_1.isUserRejection)(error_7)) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__WALLET_INTERACTION__USER_REJECTED_TRANSACTION, { walletName: 'Standard Wallet' });
                    }
                    // For other errors, wrap with generic signing error
                    throw (0, errors_1.wrapError)(error_7, errors_1.HERMIS_ERROR__SIGNING__TRANSACTION_FAILED, {
                        reason: 'Failed to sign transaction with Standard Wallet'
                    });
                case 6:
                    if (!result || !result[0] || !result[0].signedTransaction) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__TRANSACTION_FAILED, {
                            reason: 'No signed transaction returned from wallet'
                        });
                    }
                    // Deserialize the signed transaction
                    try {
                        signedTransactionBytes = result[0].signedTransaction;
                        if (isVersionedTransaction(transaction)) {
                            signedTransactions.push(web3_js_1.VersionedTransaction.deserialize(signedTransactionBytes));
                        }
                        else {
                            signedTransactions.push(web3_js_1.Transaction.from(signedTransactionBytes));
                        }
                    }
                    catch (error) {
                        throw (0, errors_1.wrapError)(error, errors_1.HERMIS_ERROR__TRANSACTION__DESERIALIZATION_FAILED, {
                            reason: 'Failed to deserialize signed transaction'
                        });
                    }
                    _c.label = 7;
                case 7:
                    _i++;
                    return [3 /*break*/, 2];
                case 8: return [2 /*return*/, signedTransactions];
                case 9:
                    // It's an Adapter
                    if (!wallet.publicKey) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__WALLET_CONNECTION__NOT_CONNECTED, { walletName: wallet.name || 'Unknown wallet' });
                    }
                    // Check for versioned transactions and verify support
                    for (_a = 0, transactions_4 = transactions; _a < transactions_4.length; _a++) {
                        transaction = transactions_4[_a];
                        if (isVersionedTransaction(transaction) &&
                            wallet.supportedTransactionVersions &&
                            !wallet.supportedTransactionVersions.has(transaction.version)) {
                            throw new errors_1.HermisError(errors_1.HERMIS_ERROR__TRANSACTION__VERSION_NOT_SUPPORTED, {
                                walletName: wallet.name || 'Unknown wallet',
                                version: String(transaction.version)
                            });
                        }
                    }
                    if (!('signAllTransactions' in wallet && typeof wallet.signAllTransactions === 'function')) return [3 /*break*/, 11];
                    return [4 /*yield*/, wallet.signAllTransactions(transactions)];
                case 10: return [2 /*return*/, _c.sent()];
                case 11:
                    if (!('signTransaction' in wallet && typeof wallet.signTransaction === 'function')) return [3 /*break*/, 16];
                    signedTransactions = [];
                    _b = 0, transactions_5 = transactions;
                    _c.label = 12;
                case 12:
                    if (!(_b < transactions_5.length)) return [3 /*break*/, 15];
                    transaction = transactions_5[_b];
                    return [4 /*yield*/, wallet.signTransaction(transaction)];
                case 13:
                    signed = _c.sent();
                    signedTransactions.push(signed);
                    _c.label = 14;
                case 14:
                    _b++;
                    return [3 /*break*/, 12];
                case 15: return [2 /*return*/, signedTransactions];
                case 16: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__TRANSACTION_FAILED, {
                    reason: 'Wallet adapter does not support transaction signing'
                });
            }
        });
    });
}
/**
 * Sends a transaction to the Solana network (supports both legacy and kit architectures)
 *
 * @param connection The Solana connection to use (supports both Legacy Connection and Kit Rpc)
 * @param transaction The transaction to send
 * @param wallet The wallet to sign with (can be Keypair, Adapter, CryptoKeyPair, or Address)
 * @param options Optional configuration for dual architecture behavior
 * @returns A promise that resolves to the transaction signature
 */
function sendTransaction(connection_1, transaction_1, wallet_1) {
    return __awaiter(this, arguments, Promise, function (connection, transaction, wallet, options) {
        var finalOptions, legacyKeypair, legacyTransaction, error_8;
        if (options === void 0) { options = {}; }
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    finalOptions = options;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 19, , 20]);
                    if (!isKitWallet(wallet)) return [3 /*break*/, 11];
                    if (!isTransactionMessage(transaction)) return [3 /*break*/, 5];
                    if (!isCryptoKeyPair(wallet)) return [3 /*break*/, 3];
                    return [4 /*yield*/, sendTransactionKit(connection, transaction, wallet)];
                case 2: 
                // Both wallet and transaction are kit architecture - sign and send
                return [2 /*return*/, _a.sent()];
                case 3: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__PRIVATE_KEY_UNAVAILABLE, {});
                case 4: return [3 /*break*/, 11];
                case 5:
                    if (!(finalOptions.fallbackToLegacy !== false)) return [3 /*break*/, 10];
                    if (!isCryptoKeyPair(wallet)) return [3 /*break*/, 8];
                    return [4 /*yield*/, convertCryptoKeyPairToKeypair(wallet)];
                case 6:
                    legacyKeypair = _a.sent();
                    return [4 /*yield*/, sendTransactionLegacy(connection, transaction, legacyKeypair, finalOptions)];
                case 7: return [2 /*return*/, _a.sent()];
                case 8: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__KIT__LEGACY_CONVERSION_FAILED, {
                    sourceType: 'Address',
                    targetType: 'LegacyWallet',
                    reason: 'Address type cannot be converted to legacy wallet for sending'
                });
                case 9: return [3 /*break*/, 11];
                case 10: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__KIT__LEGACY_MODE_INCOMPATIBLE, {
                    feature: 'Kit wallet sending legacy transaction',
                    suggestion: 'Enable fallbackToLegacy option'
                });
                case 11:
                    if (!isTransactionMessage(transaction)) return [3 /*break*/, 16];
                    if (!(finalOptions.preferKitArchitecture === false || finalOptions.fallbackToLegacy !== false)) return [3 /*break*/, 14];
                    return [4 /*yield*/, convertTransactionMessageToLegacy(transaction)];
                case 12:
                    legacyTransaction = _a.sent();
                    return [4 /*yield*/, sendTransactionLegacy(connection, legacyTransaction, wallet, finalOptions)];
                case 13: return [2 /*return*/, _a.sent()];
                case 14: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__KIT__LEGACY_MODE_INCOMPATIBLE, {
                    feature: 'Legacy wallet sending Kit transaction',
                    suggestion: 'Enable conversion or use a Kit-compatible wallet'
                });
                case 15: return [3 /*break*/, 18];
                case 16: return [4 /*yield*/, sendTransactionLegacy(connection, transaction, wallet, finalOptions)];
                case 17: 
                // Both wallet and transaction are legacy architecture
                return [2 /*return*/, _a.sent()];
                case 18: return [3 /*break*/, 20];
                case 19:
                    error_8 = _a.sent();
                    console.error('Failed to send transaction:', error_8);
                    throw error_8;
                case 20: return [2 /*return*/];
            }
        });
    });
}
/**
 * Legacy implementation for sending transactions
 */
function sendTransactionLegacy(connection_1, transaction_1, wallet_1) {
    return __awaiter(this, arguments, Promise, function (connection, transaction, wallet, options) {
        var blockhash, signedTransaction, serializedTx;
        if (options === void 0) { options = {}; }
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(!isVersionedTransaction(transaction) && !transaction.recentBlockhash)) return [3 /*break*/, 2];
                    return [4 /*yield*/, (0, index_3.getLatestBlockhash)(connection)];
                case 1:
                    blockhash = (_a.sent()).blockhash;
                    transaction.recentBlockhash = blockhash;
                    _a.label = 2;
                case 2:
                    if (!('secretKey' in wallet)) return [3 /*break*/, 11];
                    if (!!isVersionedTransaction(transaction)) return [3 /*break*/, 7];
                    if (!(0, index_1.isLegacyConnection)(connection)) return [3 /*break*/, 4];
                    return [4 /*yield*/, (0, web3_js_1.sendAndConfirmTransaction)(connection, transaction, [wallet])];
                case 3: return [2 /*return*/, _a.sent()];
                case 4:
                    // For Kit connection, sign and send manually
                    transaction.sign(wallet);
                    return [4 /*yield*/, (0, index_3.sendTransactionHelper)(connection, transaction)];
                case 5: return [2 /*return*/, _a.sent()];
                case 6: return [3 /*break*/, 10];
                case 7: return [4 /*yield*/, signVersionedTransactionWithKeypair(transaction, wallet)];
                case 8:
                    signedTransaction = _a.sent();
                    return [4 /*yield*/, (0, index_3.sendTransactionHelper)(connection, signedTransaction)];
                case 9: return [2 /*return*/, _a.sent()];
                case 10: return [3 /*break*/, 17];
                case 11:
                    if (!("features" in wallet)) return [3 /*break*/, 14];
                    if (!isTransactionSigned(transaction)) return [3 /*break*/, 13];
                    serializedTx = isVersionedTransaction(transaction)
                        ? transaction.serialize()
                        : transaction.serialize();
                    return [4 /*yield*/, (0, index_3.sendRawTransaction)(connection, serializedTx, {
                            skipPreflight: false,
                            preflightCommitment: 'confirmed'
                        })];
                case 12: return [2 /*return*/, _a.sent()];
                case 13: 
                // Transaction is unsigned - throw error with helpful message
                throw new errors_1.HermisError(errors_1.HERMIS_ERROR__TRANSACTION__SEND_FAILED, {
                    reason: 'Cannot send unsigned transaction with Standard Wallet. Use signAndSendTransaction() instead, or call signTransaction() first.'
                });
                case 14:
                    // It's an Adapter
                    if (!wallet.publicKey) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__WALLET_CONNECTION__NOT_CONNECTED, { walletName: wallet.name || 'Unknown wallet' });
                    }
                    if (!wallet.sendTransaction) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__INVARIANT__OPERATION_NOT_ALLOWED, { operation: 'sendTransaction', reason: 'Wallet does not support sending transactions' });
                    }
                    // Set fee payer if not already set (for regular transactions)
                    if (!isVersionedTransaction(transaction) && !transaction.feePayer) {
                        transaction.feePayer = wallet.publicKey;
                    }
                    // Check for versioned transaction support
                    if (isVersionedTransaction(transaction) &&
                        wallet.supportedTransactionVersions &&
                        !wallet.supportedTransactionVersions.has(transaction.version)) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__TRANSACTION__VERSION_NOT_SUPPORTED, {
                            walletName: wallet.name || 'Unknown wallet',
                            version: String(transaction.version)
                        });
                    }
                    if (!(0, index_1.isLegacyConnection)(connection)) return [3 /*break*/, 16];
                    return [4 /*yield*/, wallet.sendTransaction(transaction, connection)];
                case 15: return [2 /*return*/, _a.sent()];
                case 16: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__INVARIANT__INVALID_ARGUMENT, {
                    argumentName: 'connection',
                    receivedValue: 'Kit Rpc',
                    expectedType: 'Legacy Connection'
                });
                case 17: return [2 /*return*/];
            }
        });
    });
}
/**
 * Signs a message using the specified wallet (supports both legacy and kit architectures)
 * @param message The message to sign
 * @param wallet The wallet to sign with (can be Keypair, Adapter, CryptoKeyPair, or Address)
 * @param options Optional configuration for dual architecture behavior
 * @returns A promise that resolves to the signed message bytes
 */
function signMessage(message_1, wallet_1) {
    return __awaiter(this, arguments, Promise, function (message, wallet, options) {
        var messageBytes, offchainBytes, messageSigner, error_9;
        if (options === void 0) { options = {}; }
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 8, , 9]);
                    messageBytes = typeof message === 'string'
                        ? new TextEncoder().encode(message)
                        : message;
                    if (!isKitWallet(wallet)) return [3 /*break*/, 6];
                    offchainBytes = (0, offchain_message_1.withOffchainMessagePrefix)(messageBytes);
                    if (!isMessagePartialSigner(wallet)) return [3 /*break*/, 2];
                    return [4 /*yield*/, signMessageKit(offchainBytes, wallet)];
                case 1: 
                // It's already a MessagePartialSigner (KeyPairSigner)
                return [2 /*return*/, _a.sent()];
                case 2:
                    if (!isCryptoKeyPair(wallet)) return [3 /*break*/, 5];
                    return [4 /*yield*/, convertCryptoKeyPairToMessageSigner(wallet)];
                case 3:
                    messageSigner = _a.sent();
                    return [4 /*yield*/, signMessageKit(offchainBytes, messageSigner)];
                case 4: return [2 /*return*/, _a.sent()];
                case 5:
                    if (typeof wallet === 'string') {
                        // Address without private key - cannot sign
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__PRIVATE_KEY_UNAVAILABLE, {});
                    }
                    _a.label = 6;
                case 6: return [4 /*yield*/, signMessageLegacy(messageBytes, wallet)];
                case 7: 
                // Everything else uses legacy implementation (Keypair, Adapter, Standard Wallet).
                // The Keypair branch inside signMessageLegacy applies the off-chain prefix
                // itself; external wallets (Standard Wallet / Adapter) apply their own.
                return [2 /*return*/, _a.sent()];
                case 8:
                    error_9 = _a.sent();
                    console.error('Failed to sign message:', error_9);
                    throw error_9;
                case 9: return [2 /*return*/];
            }
        });
    });
}
/**
 * Kit implementation for message signing using @solana/kit
 */
function signMessageKit(messageBytes, signer) {
    return __awaiter(this, void 0, Promise, function () {
        var signableMessage, signatureDictionary, signature, error_10;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    signableMessage = (0, kit_1.createSignableMessage)(messageBytes);
                    return [4 /*yield*/, signer.signMessages([signableMessage])];
                case 1:
                    signatureDictionary = (_a.sent())[0];
                    signature = signatureDictionary[signer.address];
                    if (!signature) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__SIGNATURE_NOT_FOUND, {
                            address: signer.address || 'unknown'
                        });
                    }
                    return [2 /*return*/, signature];
                case 2:
                    error_10 = _a.sent();
                    throw (0, errors_1.wrapError)(error_10, errors_1.HERMIS_ERROR__SIGNING__MESSAGE_FAILED, {
                        reason: 'Kit message signing failed'
                    });
                case 3: return [2 /*return*/];
            }
        });
    });
}
/**
 * Kit implementation for transaction signing using @solana/kit with direct signer
 * This follows the exact pattern from @solana/signers/sign-transaction.ts
 *
 * Key Points:
 * - Input: TransactionMessage (unsigned transaction message)
 * - Output: Transaction (compiled transaction with signatures)
 * - Process: compile → sign messageBytes → attach signatures → return Transaction
 *
 * @param transactionMessage The TransactionMessage to sign
 * @param signer The MessagePartialSigner to sign with (must be KeyPairSigner or compatible)
 * @returns The signed Transaction (NOT TransactionMessage)
 */
function signTransactionWithKitSigner(transactionMessage, signer) {
    return __awaiter(this, void 0, Promise, function () {
        var transaction, messageBytes, signableMessage, signatureDictionaries, signatureDict, signature, signedTransaction, error_11;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    transaction = (0, kit_1.compileTransaction)(transactionMessage);
                    messageBytes = transaction.messageBytes;
                    if (!messageBytes) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__TRANSACTION__COMPILATION_FAILED, {
                            reason: 'Failed to get message bytes from compiled transaction'
                        });
                    }
                    signableMessage = (0, kit_1.createSignableMessage)(messageBytes);
                    return [4 /*yield*/, signer.signMessages([signableMessage])];
                case 1:
                    signatureDictionaries = _b.sent();
                    if (!signatureDictionaries || signatureDictionaries.length === 0) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__SIGNATURE_NOT_FOUND, {
                            address: 'unknown'
                        });
                    }
                    signatureDict = signatureDictionaries[0];
                    signature = signatureDict[signer.address];
                    if (!signature) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__SIGNATURE_NOT_FOUND, {
                            address: signer.address
                        });
                    }
                    signedTransaction = __assign(__assign({}, transaction), { signatures: __assign(__assign({}, (transaction.signatures || {})), (_a = {}, _a[signer.address] = signature, _a)) });
                    // Return the signed Transaction object
                    // Note: This is a Transaction, not a TransactionMessage
                    return [2 /*return*/, signedTransaction];
                case 2:
                    error_11 = _b.sent();
                    throw (0, errors_1.wrapError)(error_11, errors_1.HERMIS_ERROR__SIGNING__TRANSACTION_FAILED, {
                        reason: 'Kit transaction signing with signer failed'
                    });
                case 3: return [2 /*return*/];
            }
        });
    });
}
/**
 * Signs a legacy transaction (Transaction or VersionedTransaction) using a Kit signer
 * This enables Kit signers to sign legacy transactions without extracting private keys
 * @param transaction The legacy transaction to sign
 * @param signer The MessagePartialSigner (KeyPairSigner) to sign with
 * @returns The signed transaction
 */
function signTransactionLegacyWithKitSigner(transaction, signer) {
    return __awaiter(this, void 0, Promise, function () {
        var cryptoKeyPair, messageBytesRaw, messageBytes, signatureArrayBuffer, signatureBytes, publicKeyBuffer, publicKeyBytes, publicKey, signature, signedTransaction, error_12;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 3, , 4]);
                    cryptoKeyPair = signer.keyPair;
                    if (!cryptoKeyPair || !cryptoKeyPair.privateKey) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__KEYPAIR_CONVERSION_FAILED, {
                            reason: 'Signer does not contain a CryptoKeyPair with private key'
                        });
                    }
                    messageBytesRaw = isVersionedTransaction(transaction)
                        ? transaction.message.serialize()
                        : transaction.serializeMessage();
                    messageBytes = messageBytesRaw instanceof Uint8Array
                        ? messageBytesRaw
                        : new Uint8Array(messageBytesRaw);
                    return [4 /*yield*/, crypto.subtle.sign('Ed25519', cryptoKeyPair.privateKey, messageBytes)];
                case 1:
                    signatureArrayBuffer = _a.sent();
                    signatureBytes = new Uint8Array(signatureArrayBuffer);
                    return [4 /*yield*/, crypto.subtle.exportKey('raw', cryptoKeyPair.publicKey)];
                case 2:
                    publicKeyBuffer = _a.sent();
                    publicKeyBytes = new Uint8Array(publicKeyBuffer);
                    publicKey = new web3_js_1.PublicKey(publicKeyBytes);
                    signature = Buffer.from(signatureBytes);
                    // Add signature to the transaction
                    if (isVersionedTransaction(transaction)) {
                        signedTransaction = new web3_js_1.VersionedTransaction(transaction.message);
                        signedTransaction.addSignature(publicKey, signature);
                        return [2 /*return*/, signedTransaction];
                    }
                    else {
                        // For legacy Transaction, add the signature
                        transaction.addSignature(publicKey, signature);
                        return [2 /*return*/, transaction];
                    }
                    return [3 /*break*/, 4];
                case 3:
                    error_12 = _a.sent();
                    throw (0, errors_1.wrapError)(error_12, errors_1.HERMIS_ERROR__SIGNING__TRANSACTION_FAILED, {
                        reason: 'Failed to sign legacy transaction with Kit signer'
                    });
                case 4: return [2 /*return*/];
            }
        });
    });
}
/**
 * Kit implementation for transaction signing using @solana/kit
 * @param transactionMessage The TransactionMessage to sign
 * @param cryptoKeyPair The CryptoKeyPair to sign with
 * @returns The signed TransactionMessage
 */
function signTransactionKit(transactionMessage, cryptoKeyPair) {
    return __awaiter(this, void 0, Promise, function () {
        var messageSigner, error_13;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 3, , 4]);
                    return [4 /*yield*/, convertCryptoKeyPairToMessageSigner(cryptoKeyPair)];
                case 1:
                    messageSigner = _a.sent();
                    return [4 /*yield*/, signTransactionWithKitSigner(transactionMessage, messageSigner)];
                case 2: 
                // Use the direct signer method
                return [2 /*return*/, _a.sent()];
                case 3:
                    error_13 = _a.sent();
                    throw (0, errors_1.wrapError)(error_13, errors_1.HERMIS_ERROR__SIGNING__TRANSACTION_FAILED, {
                        reason: 'Kit transaction signing failed'
                    });
                case 4: return [2 /*return*/];
            }
        });
    });
}
/**
 * Kit implementation for transaction sending using @solana/kit
 * @param connection The Solana connection (accepts both Legacy and Kit)
 * @param transactionMessage The TransactionMessage to send
 * @param cryptoKeyPair The CryptoKeyPair to sign with
 * @returns The transaction signature
 */
function sendTransactionKit(connection, transactionMessage, cryptoKeyPair) {
    return __awaiter(this, void 0, Promise, function () {
        var signedTransaction, compiledTransaction, transactionEncoder, serializedTransaction, signature, error_14;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 3, , 4]);
                    return [4 /*yield*/, signTransactionKit(transactionMessage, cryptoKeyPair)];
                case 1:
                    signedTransaction = _a.sent();
                    compiledTransaction = (0, kit_1.compileTransaction)(signedTransaction);
                    transactionEncoder = (0, kit_1.getTransactionEncoder)();
                    serializedTransaction = transactionEncoder.encode(compiledTransaction);
                    return [4 /*yield*/, (0, index_3.sendRawTransaction)(connection, new Uint8Array(serializedTransaction), {
                            skipPreflight: false,
                            preflightCommitment: 'confirmed',
                        })];
                case 2:
                    signature = _a.sent();
                    return [2 /*return*/, signature];
                case 3:
                    error_14 = _a.sent();
                    throw (0, errors_1.wrapError)(error_14, errors_1.HERMIS_ERROR__TRANSACTION__SEND_FAILED, {
                        reason: 'Kit transaction sending failed'
                    });
                case 4: return [2 /*return*/];
            }
        });
    });
}
/**
 * Sign a VersionedTransaction with a Keypair
 * This implements manual signing for VersionedTransaction since @solana/web3.js doesn't support it directly
 */
function signVersionedTransactionWithKeypair(transaction, keypair) {
    return __awaiter(this, void 0, Promise, function () {
        var messageBytes, signature, signedTransaction;
        return __generator(this, function (_a) {
            try {
                messageBytes = transaction.message.serialize();
                signature = tweetnacl_1.default.sign.detached(messageBytes, keypair.secretKey);
                signedTransaction = new web3_js_1.VersionedTransaction(transaction.message);
                signedTransaction.addSignature(keypair.publicKey, signature);
                return [2 /*return*/, signedTransaction];
            }
            catch (error) {
                throw (0, errors_1.wrapError)(error, errors_1.HERMIS_ERROR__SIGNING__TRANSACTION_FAILED, {
                    transactionType: 'VersionedTransaction',
                    reason: 'Failed to sign VersionedTransaction with Keypair'
                });
            }
            return [2 /*return*/];
        });
    });
}
/**
 * Legacy implementation for message signing
 */
function signMessageLegacy(messageBytes, wallet) {
    return __awaiter(this, void 0, Promise, function () {
        var offchainBytes, signature, feature, result, error_15;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!('secretKey' in wallet)) return [3 /*break*/, 1];
                    offchainBytes = (0, offchain_message_1.withOffchainMessagePrefix)(messageBytes);
                    signature = tweetnacl_1.default.sign.detached(offchainBytes, wallet.secretKey);
                    return [2 /*return*/, signature];
                case 1:
                    if (!("_connectedAccount" in wallet && "_wallet" in wallet)) return [3 /*break*/, 6];
                    feature = wallet._wallet.features[wallet_standard_types_1.SolanaSignMessageMethod];
                    if (!feature || typeof feature.signMessage !== 'function') {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__STANDARD_WALLET__FEATURE_NOT_FOUND, { featureName: 'signMessage', walletName: wallet._wallet.name || 'Unknown wallet' });
                    }
                    _a.label = 2;
                case 2:
                    _a.trys.push([2, 4, , 5]);
                    return [4 /*yield*/, feature.signMessage({
                            message: messageBytes,
                            account: wallet._connectedAccount[0]
                        })];
                case 3:
                    result = _a.sent();
                    if (!result || result.length < 1 || !result[0].signature) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__MESSAGE_FAILED, { reason: 'No signature returned from signMessage' });
                    }
                    return [2 /*return*/, result[0].signature];
                case 4:
                    error_15 = _a.sent();
                    // Check if user rejected the signature
                    if ((0, errors_1.isUserRejection)(error_15)) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__WALLET_INTERACTION__USER_REJECTED_SIGNATURE, { walletName: wallet._wallet.name || 'Unknown wallet' });
                    }
                    // For other errors, wrap with generic signing error
                    throw (0, errors_1.wrapError)(error_15, errors_1.HERMIS_ERROR__SIGNING__MESSAGE_FAILED, {
                        reason: 'Failed to sign message with Standard Wallet'
                    });
                case 5: return [3 /*break*/, 7];
                case 6: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__INVARIANT__INVALID_ARGUMENT, { argumentName: 'wallet', expectedType: 'Keypair or Standard Wallet', receivedValue: typeof wallet });
                case 7: return [2 /*return*/];
            }
        });
    });
}
/**
 * Performs a Sign-In with Solana (SIWS) operation (supports both legacy and kit architectures)
 *
 * @param wallet The wallet to use for signing (can be Adapter, CryptoKeyPair, or Address)
 * @param input Optional sign-in parameters
 * @param options Optional configuration for dual architecture behavior
 * @returns A promise that resolves to the sign-in result
 */
function signIn(wallet_1) {
    return __awaiter(this, arguments, Promise, function (wallet, input, options) {
        var signInMessage, messageBytes, error_16;
        if (input === void 0) { input = {}; }
        if (options === void 0) { options = {}; }
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 3, , 4]);
                    // Handle kit architecture
                    if (isKitWallet(wallet)) {
                        if (isCryptoKeyPair(wallet)) {
                            signInMessage = (0, index_2.generateSignInMessage)(input);
                            messageBytes = new TextEncoder().encode(signInMessage);
                            // Sign the message using kit architecture
                            // This would need proper kit message signing implementation
                            throw new errors_1.HermisError(errors_1.HERMIS_ERROR__KIT__LEGACY_MODE_INCOMPATIBLE, {
                                feature: 'Kit sign-in',
                                suggestion: 'Use legacy sign-in with an Adapter'
                            });
                        }
                        else {
                            // Address without private key - cannot sign
                            throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__PRIVATE_KEY_UNAVAILABLE, {});
                        }
                    }
                    if (!isLegacyWallet(wallet)) return [3 /*break*/, 2];
                    // Only Adapters are supported for sign-in (not raw Keypairs)
                    if ('secretKey' in wallet) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__INVARIANT__OPERATION_NOT_ALLOWED, {
                            operation: 'sign-in',
                            reason: 'Sign-in with raw Keypair is not supported - use an Adapter'
                        });
                    }
                    return [4 /*yield*/, signInLegacy(wallet, input)];
                case 1: return [2 /*return*/, _a.sent()];
                case 2: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__KIT__INVALID_WALLET_TYPE, {
                    walletType: typeof wallet,
                    operation: 'sign-in',
                    expectedTypes: ['Adapter', 'StandardWalletAdapter']
                });
                case 3:
                    error_16 = _a.sent();
                    console.error('Failed to sign in with Solana:', error_16);
                    throw error_16;
                case 4: return [2 /*return*/];
            }
        });
    });
}
/**
 * Legacy implementation for Sign-In with Solana
 */
function signInLegacy(wallet_1) {
    return __awaiter(this, arguments, Promise, function (wallet, input) {
        var result, signature_1, signedMessage, account, resultAny, error_17, signInMessage, messageBytes, signature, domain, nonce, chainId, version;
        if (input === void 0) { input = {}; }
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    // Check if wallet is connected
                    if (!wallet.publicKey) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__WALLET_CONNECTION__NOT_CONNECTED, { walletName: wallet.name || 'Unknown wallet' });
                    }
                    if (!('signIn' in wallet && typeof wallet.signIn === 'function')) return [3 /*break*/, 4];
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, wallet.signIn(input)];
                case 2:
                    result = _a.sent();
                    signature_1 = result.signature;
                    signedMessage = result.signedMessage;
                    account = result.account;
                    resultAny = result;
                    if (!signature_1 && resultAny['0']) {
                        signature_1 = resultAny['0'].signature;
                        signedMessage = resultAny['0'].signedMessage;
                        account = account || resultAny['0'].account;
                    }
                    // If we have valid signature and signedMessage, return them
                    if (signature_1 && signedMessage) {
                        return [2 /*return*/, {
                                account: account,
                                signature: signature_1,
                                signedMessage: signedMessage,
                                domain: input.domain || (typeof window !== 'undefined' ? window.location.host : 'unknown'),
                                nonce: input.nonce || (0, index_2.generateNonce)(),
                                statement: input.statement,
                                version: input.version || '1'
                            }];
                    }
                    return [3 /*break*/, 4];
                case 3:
                    error_17 = _a.sent();
                    // Wallet's native signIn failed, fall through to manual signing
                    console.warn('Wallet native signIn failed, falling back to manual signing:', error_17);
                    return [3 /*break*/, 4];
                case 4:
                    // Check if wallet at least supports signMessage
                    if (!('signMessage' in wallet) || typeof wallet.signMessage !== 'function') {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__MESSAGE_FAILED, { reason: 'Wallet does not support message signing' });
                    }
                    signInMessage = (0, index_2.generateSignInMessage)(input);
                    messageBytes = new TextEncoder().encode(signInMessage);
                    return [4 /*yield*/, wallet.signMessage(messageBytes)];
                case 5:
                    signature = _a.sent();
                    domain = input.domain || (typeof window !== 'undefined' ? window.location.host : 'unknown');
                    nonce = input.nonce || (0, index_2.generateNonce)();
                    chainId = input.chainId || 'solana:mainnet';
                    version = input.version || '1';
                    return [2 /*return*/, {
                            account: {
                                address: wallet.publicKey.toBase58(),
                                publicKey: new Uint8Array(wallet.publicKey.toBytes()),
                                chain: chainId,
                            },
                            signature: signature,
                            signedMessage: messageBytes,
                            domain: domain,
                            nonce: nonce,
                            statement: input.statement,
                            version: version,
                        }];
            }
        });
    });
}
// Helper functions for working with dual architectures
/**
 * Creates a kit transaction message with fee payer and lifetime
 * This is a helper function to demonstrate how to create proper kit transactions
 */
function createKitTransaction(connection_1, feePayer_1) {
    return __awaiter(this, arguments, Promise, function (connection, feePayer, instructions) {
        var _a, blockhash, lastValidBlockHeight, transactionMessage, _i, instructions_1, instruction, recentBlockhash;
        if (instructions === void 0) { instructions = []; }
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, (0, index_3.getLatestBlockhash)(connection)];
                case 1:
                    _a = _b.sent(), blockhash = _a.blockhash, lastValidBlockHeight = _a.lastValidBlockHeight;
                    transactionMessage = (0, kit_1.createTransactionMessage)({ version: 0 });
                    // Add instructions if provided
                    for (_i = 0, instructions_1 = instructions; _i < instructions_1.length; _i++) {
                        instruction = instructions_1[_i];
                        transactionMessage = (0, kit_1.appendTransactionMessageInstruction)(instruction, transactionMessage);
                    }
                    // Set fee payer
                    transactionMessage = (0, kit_1.setTransactionMessageFeePayer)(feePayer, transactionMessage);
                    recentBlockhash = {
                        blockhash: blockhash,
                        lastValidBlockHeight: BigInt(lastValidBlockHeight),
                    };
                    transactionMessage = (0, kit_1.setTransactionMessageLifetimeUsingBlockhash)(recentBlockhash, transactionMessage);
                    return [2 /*return*/, transactionMessage];
            }
        });
    });
}
/**
 * Utility to generate a kit keypair signer directly
 * This bypasses the CryptoKeyPair extraction issues by using kit's internal methods
 */
function generateKeyPairSigner() {
    return __awaiter(this, void 0, Promise, function () {
        var privateKeyBytes;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    privateKeyBytes = crypto.getRandomValues(new Uint8Array(32));
                    return [4 /*yield*/, (0, kit_1.createKeyPairSignerFromPrivateKeyBytes)(privateKeyBytes)];
                case 1: 
                // Create a signer directly from the private key bytes
                return [2 /*return*/, _a.sent()];
            }
        });
    });
}
/**
 * Generate a kit keypair with address for easier access
 * Returns both a CryptoKeyPair (for compatibility) and a working signer
 */
function generateKitKeypair() {
    return __awaiter(this, void 0, Promise, function () {
        var signer, keypair;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, generateKeyPairSigner()];
                case 1:
                    signer = _a.sent();
                    return [4 /*yield*/, (0, kit_1.generateKeyPair)()];
                case 2:
                    keypair = _a.sent();
                    return [2 /*return*/, { keypair: keypair, address: signer.address, signer: signer }];
            }
        });
    });
}
/**
 * Utility to check if a wallet supports kit architecture
 */
function supportsKitArchitecture(wallet) {
    return isKitWallet(wallet);
}
/**
 * Utility to check if a transaction is kit architecture
 */
function isKitTransaction(transaction) {
    return isTransactionMessage(transaction);
}
/**
 * Create RPC connection helper function
 * This provides a more convenient way to create connections for transaction operations
 * @param rpcUrl The RPC URL to connect to
 * @param options Optional connection configuration
 * @returns A Connection instance configured for transaction operations
 */
function createRPCConnection(rpcUrl, options) {
    return (0, index_3.createConnection)(rpcUrl, __assign({ commitment: 'confirmed', confirmTransactionInitialTimeout: 60000 }, options));
}
/**
 * Send transaction with automatic RPC connection creation
 * @param rpcUrl The RPC URL to use for the connection
 * @param transaction The transaction to send
 * @param wallet The wallet to sign with
 * @param options Optional dual architecture options
 * @returns Promise resolving to transaction signature
 */
function sendTransactionWithRPC(rpcUrl_1, transaction_1, wallet_1) {
    return __awaiter(this, arguments, Promise, function (rpcUrl, transaction, wallet, options) {
        var connection;
        if (options === void 0) { options = {}; }
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    connection = createRPCConnection(rpcUrl);
                    return [4 /*yield*/, sendTransaction(connection, transaction, wallet, options)];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
/**
 * Sign transaction with explicit signer support (improved compatibility)
 * @param transaction The transaction to sign
 * @param signer The signer to use (supports all signer types)
 * @param options Optional dual architecture options
 * @returns Promise resolving to signed transaction
 */
function signTransactionWithSigner(transaction_1, signer_1) {
    return __awaiter(this, arguments, Promise, function (transaction, signer, options) {
        if (options === void 0) { options = {}; }
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, signTransaction(transaction, signer, options)];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    });
}
/**
 * Sign and send a transaction (supports both legacy and kit architectures).
 *
 * @param connection The Solana connection to use (supports both legacy Connection and Kit Rpc)
 * @param transaction The transaction to sign and send
 * @param wallet The wallet to sign with (can be Keypair, Adapter, CryptoKeyPair, or Address)
 * @param options Optional configuration for dual architecture behavior
 */
function signAndSendTransaction(connection_1, transaction_1, wallet_1) {
    return __awaiter(this, arguments, Promise, function (connection, transaction, wallet, options) {
        var finalOptions, legacyKeypair, standardWallet, feature, account, transactionBytes, chain, result, legacyTransaction, error_18;
        if (options === void 0) { options = {}; }
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    finalOptions = options;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 21, , 22]);
                    if (!isKitWallet(wallet)) return [3 /*break*/, 11];
                    if (!isTransactionMessage(transaction)) return [3 /*break*/, 5];
                    if (!isCryptoKeyPair(wallet)) return [3 /*break*/, 3];
                    return [4 /*yield*/, signAndSendTransactionKit(connection, transaction, wallet)];
                case 2: 
                // Both wallet and transaction are kit architecture - sign and send directly
                return [2 /*return*/, _a.sent()];
                case 3: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__PRIVATE_KEY_UNAVAILABLE, {});
                case 4: return [3 /*break*/, 11];
                case 5:
                    if (!(finalOptions.fallbackToLegacy !== false)) return [3 /*break*/, 10];
                    if (!isCryptoKeyPair(wallet)) return [3 /*break*/, 8];
                    return [4 /*yield*/, convertCryptoKeyPairToKeypair(wallet)];
                case 6:
                    legacyKeypair = _a.sent();
                    return [4 /*yield*/, signAndSendTransactionLegacy(connection, transaction, legacyKeypair, finalOptions)];
                case 7: return [2 /*return*/, _a.sent()];
                case 8: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__KIT__LEGACY_CONVERSION_FAILED, {
                    sourceType: 'Address',
                    targetType: 'LegacyWallet',
                    reason: 'Address type cannot be converted to legacy wallet for signing and sending'
                });
                case 9: return [3 /*break*/, 11];
                case 10: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__KIT__LEGACY_MODE_INCOMPATIBLE, {
                    feature: 'Kit wallet sign and send legacy transaction',
                    suggestion: 'Enable fallbackToLegacy option'
                });
                case 11:
                    if (!isTransactionMessage(transaction)) return [3 /*break*/, 18];
                    if (!("_connectedAccount" in wallet && "_wallet" in wallet)) return [3 /*break*/, 13];
                    standardWallet = wallet;
                    feature = standardWallet._wallet.features[wallet_standard_types_1.SolanaSignAndSendTransactionMethod];
                    if (!feature || typeof feature.signAndSendTransaction !== 'function') {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__STANDARD_WALLET__FEATURE_NOT_FOUND, { featureName: 'signAndSendTransaction', walletName: standardWallet._wallet.name || 'Unknown wallet' });
                    }
                    account = standardWallet._connectedAccount[0];
                    if (!account) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__STANDARD_WALLET__ACCOUNT_NOT_FOUND, { walletName: standardWallet._wallet.name || 'Unknown wallet' });
                    }
                    transactionBytes = void 0;
                    try {
                        transactionBytes = serializeTransactionForWallet(transaction);
                    }
                    catch (error) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__TRANSACTION__SERIALIZATION_FAILED, {
                            transactionType: 'Kit TransactionMessage',
                            reason: 'Serialization failed for Standard Wallet',
                            originalError: error instanceof Error ? error.message : String(error)
                        }, error instanceof Error ? error : undefined);
                    }
                    chain = finalOptions.chain || 'solana:devnet';
                    return [4 /*yield*/, feature.signAndSendTransaction({
                            account: account,
                            transaction: transactionBytes,
                            chain: chain
                        })];
                case 12:
                    result = _a.sent();
                    if (!result || !result[0] || !result[0].signature) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__TRANSACTION__SEND_FAILED, { reason: 'No signature returned from signAndSendTransaction' });
                    }
                    return [2 /*return*/, bs58_1.default.encode(result[0].signature)];
                case 13:
                    if (!(finalOptions.preferKitArchitecture === false || finalOptions.fallbackToLegacy !== false)) return [3 /*break*/, 16];
                    return [4 /*yield*/, convertTransactionMessageToLegacy(transaction)];
                case 14:
                    legacyTransaction = _a.sent();
                    return [4 /*yield*/, signAndSendTransactionLegacy(connection, legacyTransaction, wallet, finalOptions)];
                case 15: return [2 /*return*/, _a.sent()];
                case 16: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__KIT__LEGACY_MODE_INCOMPATIBLE, {
                    feature: 'Legacy wallet sign and send Kit transaction',
                    suggestion: 'Enable conversion or use a Kit-compatible wallet'
                });
                case 17: return [3 /*break*/, 20];
                case 18: return [4 /*yield*/, signAndSendTransactionLegacy(connection, transaction, wallet, finalOptions)];
                case 19: 
                // Both wallet and transaction are legacy architecture
                return [2 /*return*/, _a.sent()];
                case 20: return [3 /*break*/, 22];
                case 21:
                    error_18 = _a.sent();
                    console.error('Failed to sign and send transaction:', error_18);
                    throw error_18;
                case 22: return [2 /*return*/];
            }
        });
    });
}
/**
 * Legacy implementation for signing and sending transactions
 */
function signAndSendTransactionLegacy(connection_1, transaction_1, wallet_1) {
    return __awaiter(this, arguments, Promise, function (connection, transaction, wallet, options) {
        var blockhash, signedTransaction, feature, account, accountPublicKey, transactionBytes, chain, result, error_19, signedTransaction;
        if (options === void 0) { options = {}; }
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(!isVersionedTransaction(transaction) && !transaction.recentBlockhash)) return [3 /*break*/, 2];
                    return [4 /*yield*/, (0, index_3.getLatestBlockhash)(connection)];
                case 1:
                    blockhash = (_a.sent()).blockhash;
                    transaction.recentBlockhash = blockhash;
                    _a.label = 2;
                case 2:
                    if (!('secretKey' in wallet)) return [3 /*break*/, 11];
                    if (!!isVersionedTransaction(transaction)) return [3 /*break*/, 7];
                    if (!(0, index_1.isLegacyConnection)(connection)) return [3 /*break*/, 4];
                    return [4 /*yield*/, (0, web3_js_1.sendAndConfirmTransaction)(connection, transaction, [wallet])];
                case 3: return [2 /*return*/, _a.sent()];
                case 4:
                    // For Kit connection, sign and send manually
                    transaction.sign(wallet);
                    return [4 /*yield*/, (0, index_3.sendTransactionHelper)(connection, transaction)];
                case 5: return [2 /*return*/, _a.sent()];
                case 6: return [3 /*break*/, 10];
                case 7: return [4 /*yield*/, signVersionedTransactionWithKeypair(transaction, wallet)];
                case 8:
                    signedTransaction = _a.sent();
                    return [4 /*yield*/, (0, index_3.sendTransactionHelper)(connection, signedTransaction)];
                case 9: return [2 /*return*/, _a.sent()];
                case 10: return [3 /*break*/, 22];
                case 11:
                    if (!("_connectedAccount" in wallet && "_wallet" in wallet)) return [3 /*break*/, 16];
                    feature = wallet._wallet.features[wallet_standard_types_1.SolanaSignAndSendTransactionMethod];
                    if (!feature || typeof feature.signAndSendTransaction !== 'function') {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__STANDARD_WALLET__FEATURE_NOT_FOUND, { featureName: 'signAndSendTransaction', walletName: wallet._wallet.name || 'Unknown wallet' });
                    }
                    account = wallet._connectedAccount[0];
                    if (!account) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__STANDARD_WALLET__ACCOUNT_NOT_FOUND, { walletName: wallet._wallet.name || 'Unknown wallet' });
                    }
                    accountPublicKey = new web3_js_1.PublicKey(account.publicKey);
                    // Ensure transaction has feePayer set (only for legacy Transaction, not Kit TransactionMessage)
                    if (!isVersionedTransaction(transaction) && !isTransactionMessage(transaction) && !transaction.feePayer) {
                        transaction.feePayer = accountPublicKey;
                    }
                    transactionBytes = void 0;
                    try {
                        transactionBytes = serializeTransactionForWallet(transaction);
                    }
                    catch (error) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__TRANSACTION__SERIALIZATION_FAILED, {
                            transactionType: isTransactionMessage(transaction) ? 'Kit TransactionMessage' : isVersionedTransaction(transaction) ? 'VersionedTransaction' : 'Transaction',
                            reason: 'Serialization failed for Standard Wallet',
                            originalError: error instanceof Error ? error.message : String(error)
                        }, error instanceof Error ? error : undefined);
                    }
                    chain = options.chain || getChainFromConnection(connection);
                    result = void 0;
                    _a.label = 12;
                case 12:
                    _a.trys.push([12, 14, , 15]);
                    return [4 /*yield*/, feature.signAndSendTransaction({
                            account: account,
                            transaction: transactionBytes,
                            chain: chain
                        })];
                case 13:
                    result = _a.sent();
                    return [3 /*break*/, 15];
                case 14:
                    error_19 = _a.sent();
                    // Check if user rejected the transaction
                    if ((0, errors_1.isUserRejection)(error_19)) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__WALLET_INTERACTION__USER_REJECTED_TRANSACTION, { walletName: wallet._wallet.name || 'Unknown wallet' });
                    }
                    // For other errors, wrap with generic transaction error
                    throw (0, errors_1.wrapError)(error_19, errors_1.HERMIS_ERROR__TRANSACTION__SEND_FAILED, {
                        reason: 'Failed to sign and send transaction with Standard Wallet'
                    });
                case 15:
                    if (!result || !result[0] || !result[0].signature) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__SIGNATURE_NOT_FOUND, {
                            address: accountPublicKey.toBase58()
                        });
                    }
                    // Convert signature bytes to base58 string
                    return [2 /*return*/, bs58_1.default.encode(result[0].signature)];
                case 16:
                    // It's an Adapter - sign then send separately
                    if (!wallet.publicKey) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__WALLET_CONNECTION__NOT_CONNECTED, {});
                    }
                    if (!wallet.sendTransaction) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__TRANSACTION__SEND_FAILED, {
                            reason: 'Wallet does not support sending transactions'
                        });
                    }
                    // Set fee payer if not already set (for regular transactions)
                    if (!isVersionedTransaction(transaction) && !transaction.feePayer) {
                        transaction.feePayer = wallet.publicKey;
                    }
                    // Check for versioned transaction support
                    if (isVersionedTransaction(transaction) &&
                        wallet.supportedTransactionVersions &&
                        !wallet.supportedTransactionVersions.has(transaction.version)) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__TRANSACTION__VERSION_NOT_SUPPORTED, {
                            walletName: wallet.name || 'Unknown wallet',
                            version: String(transaction.version)
                        });
                    }
                    // Check if adapter supports signAndSendTransaction
                    // Note: Adapter expects legacy Connection, so only works with legacy connection
                    if (!(0, index_1.isLegacyConnection)(connection)) {
                        throw new errors_1.HermisError(errors_1.HERMIS_ERROR__INVARIANT__INVALID_ARGUMENT, {
                            argumentName: 'connection',
                            receivedValue: 'Kit Rpc',
                            expectedType: 'Legacy Connection'
                        });
                    }
                    if (!('signAndSendTransaction' in wallet && typeof wallet.signAndSendTransaction === 'function')) return [3 /*break*/, 18];
                    return [4 /*yield*/, wallet.signAndSendTransaction(transaction, connection)];
                case 17: return [2 /*return*/, _a.sent()];
                case 18:
                    if (!('signTransaction' in wallet && typeof wallet.signTransaction === 'function')) return [3 /*break*/, 21];
                    return [4 /*yield*/, wallet.signTransaction(transaction)];
                case 19:
                    signedTransaction = _a.sent();
                    return [4 /*yield*/, wallet.sendTransaction(signedTransaction, connection)];
                case 20: return [2 /*return*/, _a.sent()];
                case 21: throw new errors_1.HermisError(errors_1.HERMIS_ERROR__SIGNING__TRANSACTION_FAILED, {
                    reason: 'Wallet adapter does not support transaction signing or sending'
                });
                case 22: return [2 /*return*/];
            }
        });
    });
}
/**
 * Kit implementation for signing and sending transactions using @solana/kit
 * @param connection The Solana connection (accepts both Legacy and Kit)
 * @param transactionMessage The TransactionMessage to sign and send
 * @param cryptoKeyPair The CryptoKeyPair to sign with
 * @returns The transaction signature
 */
function signAndSendTransactionKit(connection, transactionMessage, cryptoKeyPair) {
    return __awaiter(this, void 0, Promise, function () {
        var signedTransaction, compiledTransaction, transactionEncoder, serializedTransaction, signature, error_20;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 3, , 4]);
                    return [4 /*yield*/, signTransactionKit(transactionMessage, cryptoKeyPair)];
                case 1:
                    signedTransaction = _a.sent();
                    compiledTransaction = (0, kit_1.compileTransaction)(signedTransaction);
                    transactionEncoder = (0, kit_1.getTransactionEncoder)();
                    serializedTransaction = transactionEncoder.encode(compiledTransaction);
                    return [4 /*yield*/, (0, index_3.sendRawTransaction)(connection, new Uint8Array(serializedTransaction), {
                            skipPreflight: false,
                            preflightCommitment: 'confirmed',
                        })];
                case 2:
                    signature = _a.sent();
                    return [2 /*return*/, signature];
                case 3:
                    error_20 = _a.sent();
                    throw (0, errors_1.wrapError)(error_20, errors_1.HERMIS_ERROR__TRANSACTION__SEND_FAILED, {
                        reason: 'Kit sign and send transaction failed'
                    });
                case 4: return [2 /*return*/];
            }
        });
    });
}
